import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { GenerationGlow } from './GenerationGlow';
import type { Scene } from '@flobi/artwork-core';
import type { CanvasHandle, ScreenBox } from '../useEditorCanvas';
import { terminal, type Binding } from './contracts';

export function generationStatusText(binding: Binding, live?: LiveRun) {
  if (binding.status === 'requesting') return 'Starting generation…';
  // A live frame is newer than the last status read (the socket sets status by event).
  if (!terminal(binding.status) && live?.stage === 'uploading') return 'Saving your result…';
  if ((binding.status === 'pending' || binding.status === 'queued') && !live?.stage && live?.progress === undefined) return 'Queued for generation…';
  if (binding.status === 'saving') return 'Saving your result…';
  if (binding.status === 'completed') return 'Result ready';
  if (binding.status === 'failed') return 'Generation failed';
  if (binding.status === 'canceled' || binding.status === 'cancelled') return 'Generation stopped';
  return `Generating ${binding.request.quantity > 1 ? `${binding.request.quantity} ${binding.request.kind === 'video' ? 'videos' : 'images'}` : binding.request.kind === 'video' ? 'video' : 'image'}…`;
}

/** What the socket said about a run; never stored in the file. */
export interface LiveRun { progress?: number; stage?: string; startedAt?: number }

// Flow Playground's progress (GenerationsGallery `useSmoothProgress`): the server's sparse
// percentages joined up on the quote's own estimate, monotonic, never past the uploading
// checkpoint until the run lands. Six times the estimate (at least ten minutes) with
// nothing new = say so, rather than hold a bar at 94%.
const CEILING = 94, CURVE = 1.6, FALLBACK_ETA = 60, STALL_FACTOR = 6, STALL_FLOOR = 600;
const STAGE_WORD: Record<string, string> = { queued: 'Starting', generating: 'Generating', uploading: 'Saving' };

/** One reading of the curve. `floor` is what was already shown: the bar never goes back. */
export function runProgress(binding: Binding, live: LiveRun | undefined, now: number, floor = 0) {
  const eta = binding.etaSeconds || FALLBACK_ETA;
  const reported = typeof live?.progress === 'number' ? Math.max(0, Math.min(100, live.progress)) : 0;
  const elapsed = Math.max(0, (now - (live?.startedAt || binding.createdAt)) / 1000);
  let value = reported;
  if (binding.status !== 'requesting' && reported < 95) value = Math.max(value, CEILING * (1 - Math.exp(-CURVE * elapsed / eta)));
  if (binding.status === 'saving') value = Math.max(value, 95);
  value = Math.max(value, floor);
  const stalled = (now - binding.createdAt) / 1000 > Math.max(STALL_FLOOR, eta * STALL_FACTOR);
  return { value, percent: Math.round(value), stalled, stage: live?.stage ? STAGE_WORD[live.stage] || live.stage : null };
}

export function useRunProgress(binding: Binding | undefined, live: LiveRun | undefined) {
  const [, tick] = useState(0);
  const shown = useRef({ key: '', value: 0 });
  const active = !!binding && !terminal(binding.status);
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => tick(n => n + 1), 250);
    return () => clearInterval(timer);
  }, [active, binding?.key]);
  if (!binding || !active) return null;
  if (shown.current.key !== binding.key) shown.current = { key: binding.key, value: 0 };
  const reading = runProgress(binding, live, Date.now(), shown.current.value);
  shown.current.value = reading.value;
  return reading;
}

export function useGenerationElapsed(binding: Binding | undefined) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!binding || terminal(binding.status)) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [binding?.key, binding?.status]);
  const seconds = binding ? Math.max(0, Math.floor((now - binding.createdAt) / 1000)) : 0;
  return seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`;
}

/** "about 12s left" from the quote's estimate; nothing when there is none to go on. */
export function timeLeft(binding: Binding, progress: { stalled: boolean; stage?: string | null } | null, now = Date.now()) {
  if (!progress || binding.status === 'requesting') return '';
  if (progress.stalled) return 'taking longer than expected';
  // Saving is the last step whatever the estimate said: the socket's stage is newer than the status.
  if (binding.status === 'saving' || progress.stage === 'Saving') return 'almost done';
  if (!binding.etaSeconds) return '';
  const left = Math.round(binding.etaSeconds - (now - binding.createdAt) / 1000);
  if (left <= 2) return 'almost done';
  return left >= 60 ? `about ${Math.round(left / 60)}m left` : `about ${left}s left`;
}

/**
 * A run on the canvas: Flow's generating light round the shape (GenerationGlow) and one line in
 * its bottom corners — what is happening, how far, how long (Tarek, 14 Oct: "remove this stupid
 * box and use text in the bottom corner to show the progress and ETA"). No Stop: a run that started
 * is paid for (Tarek, 2 Nov: "users can't stop a generation once it started").
 * Measures after the canvas frame, and follows pan/zoom without relying on selection.
 */
export function GenerationCanvasStatus({ binding, live, canvas, scene, onReview }: {
  binding: Binding; live?: LiveRun; canvas: CanvasHandle; scene: Scene; onReview(): void;
}) {
  const progress = useRunProgress(binding, live);
  const [geometry, setGeometry] = useState<{ box: ScreenBox; room: ScreenBox } | null>(null);
  useGenerationElapsed(binding); // ticks the time left once a second
  const active = !terminal(binding.status);
  // Stopped by someone is not a failure: no red, and the status alone beside Review.
  const stopped = binding.status === 'canceled' || binding.status === 'cancelled';
  const created = binding.createdTarget ?? !binding.request.destination?.targetId;
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      const box = canvas.boxOf(binding.targetId), room = canvas.generationViewport();
      setGeometry(box && room ? { box, room } : null);
    };
    // Committed inside the frame, or the box lands a frame behind the canvas under it for the whole zoom.
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => flushSync(measure)); };
    measure(); schedule();
    const offView = canvas.onViewChange(schedule), offScene = scene.subscribe(schedule);
    return () => { offView(); offScene(); cancelAnimationFrame(frame); };
  }, [canvas, scene, binding.targetId]);
  if (!geometry) return null;
  const { box, room } = geometry;
  if (box.right <= room.left || box.left >= room.right || box.bottom <= room.top || box.top >= room.bottom) return null;
  const width = box.right - box.left, height = box.bottom - box.top;
  const tiny = width < 120 || height < 60, compact = width < 240;
  const percent = progress && !progress.stalled && binding.status !== 'requesting' ? `${progress.percent}%` : '';
  const eta = timeLeft(binding, progress);
  const words = active
    ? [compact ? '' : generationStatusText(binding, live).replace(/…$/, ''), percent, compact ? '' : eta].filter(Boolean).join(' · ') || generationStatusText(binding, live)
    : stopped ? generationStatusText(binding, live) : binding.message || generationStatusText(binding, live);
  return <div className="artwork-ai-generation-layer" style={{ left: room.left, top: room.top, width: room.right - room.left, height: room.bottom - room.top }}>
    <div className={`artwork-ai-generation-frame${created ? ' is-new' : ''}${active ? ' is-active' : stopped ? '' : ' is-failed'}`} style={{ left: box.left - room.left, top: box.top - room.top, width, height }}>
      {active && <GenerationGlow />}
      {!tiny && <div className="artwork-ai-generation-corner">
        <span role={active || stopped ? 'status' : 'alert'} aria-live="polite" title={stopped ? undefined : binding.message || undefined}>{words}</span>
        {!active && <button type="button" onClick={onReview}>Review</button>}
      </div>}
    </div>
  </div>;
}
